import React, { useRef, useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  Navigate
} from 'react-router-dom';
import { collection, getDocs } from "firebase/firestore";
import { auth, db } from "./Components/firebase";
import { onAuthStateChanged } from "firebase/auth";
import './App.css';

// Page Components
import Start from './Start';                     // <-- NEW Start with form
import Tour from './Tour';
import Map from './Components/Map';
import ProjectDetails from './Components/ProjectDetails';
import ManagementDashboard from './Components/ManagementDashboard';
import Login from './Components/Login';

function App() {
  const mountRef = useRef(null);
  const navigate = useNavigate();

  // ------------------------------------------------------------------
  // 1. Global state
  // ------------------------------------------------------------------
  const [isLoading, setIsLoading] = useState(true);
  const [plots, setPlots] = useState(null);
  const [user, setUser] = useState(null);

  // Form-related state (passed to Start)
  const [showForm, setShowForm] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobileNumber: "",
    email: "",
  });
  const [formError, setFormError] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);

  // ------------------------------------------------------------------
  // 2. Firebase Auth listener
  // ------------------------------------------------------------------
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // ------------------------------------------------------------------
  // 3. Load plots from Firestore
  // ------------------------------------------------------------------
  useEffect(() => {
    const fetchPlots = async () => {
      try {
        const plotsCollection = collection(db, "ClarksExoticaVilla");
        const snapshot = await getDocs(plotsCollection);
        const data = snapshot.docs.map(doc => ({
          projectId: doc.id,
          ...doc.data(),
        }));
        setPlots(data);
      } catch (err) {
        console.error("Failed to load plots:", err);
        setPlots([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlots();

    // Fallback: hide loader after 3s even if Firestore hangs
    const timeout = setTimeout(() => setIsLoading(false), 3000);
    return () => clearTimeout(timeout);
  }, []);

  // ------------------------------------------------------------------
  // 4. Navigation helper for Start button
  // ------------------------------------------------------------------
  const handleStart = () => {
    navigate('/explore');
  };

  // ------------------------------------------------------------------
  // 5. Props bundle for <Start />
  // ------------------------------------------------------------------
  const startProps = {
    showForm,
    setShowForm,
    showThankYou,
    setShowThankYou,
    formData,
    setFormData,
    formError,
    setFormError,
    formSubmitted,
    setFormSubmitted,
    onStart: handleStart,
  };

  // ------------------------------------------------------------------
  // 6. Loading screen
  // ------------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-container">
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // 7. Main router
  // ------------------------------------------------------------------
  return (
    <div
      ref={mountRef}
      style={{ width: '100vw', height: '100vh', overflow: 'hidden' }}
      className="app-container"
    >
      <Routes>
        {/* ROOT – Form + Start UI */}
        <Route path="/" element={<Start {...startProps} />} />

        {/* Virtual Tour */}
        <Route path="/explore" element={<Tour />} />

        {/* Booking Map (no form overlay) */}
        <Route path="/booking" element={<Map plots={plots} />} />

        {/* Project Detail */}
        <Route path="/booking/project/:projectId" element={<ProjectDetails />} />

        {/* Auth */}
        <Route path="/login" element={<Login />} />

        {/* Protected Dashboard */}
        <Route
          path="/dashboard"
          element={
            user ? <ManagementDashboard /> : <Navigate to="/login" replace />
          }
        />

        {/* Catch-all (optional) */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

// ----------------------------------------------------------------------
// 8. App Wrapper with Router
// ----------------------------------------------------------------------
export default function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}